// Pricing utility functions for v2 per-investment model

import { getToken } from './tokenManager';
import logger from './logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Get user's investment count
 */
export const getUserInvestmentCount = async (token) => {
  try {
    const authToken = token || getToken();
    const response = await fetch(`${BACKEND_URL}/api/investments`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json'
      },
    });

    if (!response.ok) return 0;
    
    const investments = await response.json();
    return Array.isArray(investments) ? investments.length : 0;
  } catch (error) {
    logger.error('Error fetching investment count:', error);
    return 0;
  }
};

/**
 * Calculate subscription cost based on investment count
 */
export const calculateSubscriptionCost = async (investmentCount, country = null, currency = 'USD') => {
  try {
    const token = getToken();
    
    const response = await fetch(`${BACKEND_URL}/api/pricing/calculate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        investment_count: investmentCount,
        country: country,
        currency: currency
      })
    });

    if (!response.ok) {
      // Fall back to v1 pricing if v2 not available
      return null;
    }

    const data = await response.json();
    // Backend returns 200 with {available:false} when no pricing_config row
    // exists for the user's region. Treat that as "no v2 pricing" so callers
    // (useSubscriptionModal etc.) fall through to v1 / N/A display.
    if (data && data.available === false) return null;
    return data;
  } catch (error) {
    logger.error('Error calculating cost:', error);
    return null;
  }
};

/**
 * Get available currencies for user's country
 */
export const getAvailableCurrencies = async (country = null) => {
  try {
    const token = getToken();
    const params = country ? `?country=${encodeURIComponent(country)}` : '';
    
    const response = await fetch(`${BACKEND_URL}/api/pricing/available-currencies${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });

    if (!response.ok) return [];
    
    const data = await response.json();
    return data.currencies || [];
  } catch (error) {
    logger.error('Error fetching currencies:', error);
    return [];
  }
};

/**
 * Get v2 pricing details for user's country
 */
export const getPricingV2 = async (country = null) => {
  try {
    const token = getToken();
    const params = country ? `?country=${encodeURIComponent(country)}` : '';
    
    const response = await fetch(`${BACKEND_URL}/api/pricing/v2${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });

    if (!response.ok) return null;
    
    return await response.json();
  } catch (error) {
    logger.error('Error fetching v2 pricing:', error);
    return null;
  }
};

/**
 * Format currency amount
 */
export const formatCurrency = (amount, currency, symbol) => {
  if (currency === 'USD' || currency === 'EUR' || currency === 'GBP') {
    return `${symbol}${amount.toFixed(2)}`;
  }
  // For NGN, INR - no decimals
  return `${symbol}${Math.round(amount).toLocaleString()}`;
};

/**
 * Check if v2 pricing is enabled for user
 */
export const isV2PricingEnabled = async () => {
  try {
    const token = getToken();
    
    // Fetch country from API instead of localStorage
    const meRes = await fetch(`${BACKEND_URL}/api/auth/me`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });
    const userData = meRes.ok ? await meRes.json() : {};
    const country = userData.country || 'USA';
    
    const response = await fetch(`${BACKEND_URL}/api/pricing/v2?country=${encodeURIComponent(country)}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
    });

    return response.ok;
  } catch (error) {
    return false;
  }
};
